'use client';

import type { ReactNode, Ref, UIEventHandler } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@lib/utils';
import { useCarousel } from '../_hooks/useCarousel.hook';
import { useMinuteClock } from '../_hooks/useMinuteClock.hook';
import ChevronButton from '../../_components/chevron-button';
import { DAY_MS } from '../../_const/stories';
import StoryCard, { type StoryCardData } from './story-card';
import './stories-board.css';

/** 공개까지 남은 날 수. 공개 시각이 KST 자정이라 올림하면 KST 달력 날짜 차이가 된다 */
function daysUntil(releaseAt: number, now: number) {
  return Math.ceil((releaseAt - now) / DAY_MS);
}

export default function StoriesBoard({
  cards,
  serverNow,
}: {
  cards: StoryCardData[];
  // 서버 렌더 시각 — 하이드레이션 때만 쓴다(첫 화면의 D-day 를 서버와 같게 그려 불일치를 막는다)
  serverNow: number;
}) {
  const t = useTranslations('TenYears.stories');

  const now = useMinuteClock() ?? serverNow;
  const carousel = useCarousel(cards.length);

  return (
    <>
      <StoryCardList
        ref={carousel.listRef}
        label={t('carouselLabel')}
        onScroll={carousel.onScroll}
      >
        {cards.map((card, i) => (
          <StoryCardSlot
            key={card.day}
            index={i}
            current={i === carousel.index}
          >
            <StoryCard card={card} dDay={daysUntil(card.releaseAt, now)} />
          </StoryCardSlot>
        ))}
      </StoryCardList>

      <div className="mt-7 flex justify-center gap-3 lg:hidden">
        <ChevronButton
          direction="prev"
          label={t('carouselPrev')}
          trackLabel="10years-stories-carousel-prev"
          disabled={carousel.atStart}
          onClick={carousel.scrollPrev}
        />
        <ChevronButton
          direction="next"
          label={t('carouselNext')}
          trackLabel="10years-stories-carousel-next"
          disabled={carousel.atEnd}
          onClick={carousel.scrollNext}
        />
      </div>
    </>
  );
}

const MOBILE_CAROUSEL =
  'mt-7 flex snap-x snap-mandatory gap-3 overflow-x-auto px-[calc(50%-150px)] py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';
const DESKTOP_MASONRY_GRID =
  'lg:mx-auto lg:mt-20 lg:grid lg:w-[960px] lg:snap-none lg:grid-cols-[repeat(3,300px)] lg:justify-between lg:gap-x-0 lg:gap-y-8 lg:overflow-visible lg:p-0';
function StoryCardList({
  ref,
  label,
  onScroll,
  children,
}: {
  ref: Ref<HTMLUListElement>;
  label: string;
  onScroll: UIEventHandler<HTMLUListElement>;
  children: ReactNode;
}) {
  return (
    <ul
      ref={ref}
      aria-label={label}
      onScroll={onScroll}
      className={cn(MOBILE_CAROUSEL, DESKTOP_MASONRY_GRID)}
    >
      {children}
    </ul>
  );
}

// Day 1~10 순. DOM 은 Day 순서 그대로(탭·낭독 순서) 두고 칸만 grid 로 옮긴다(시안 4941:28781)
const MASONRY_PLACEMENT = [
  'lg:col-start-2 lg:row-start-1',
  'lg:col-start-1 lg:row-start-1 lg:translate-y-[93px]',
  'lg:col-start-2 lg:row-start-2',
  'lg:col-start-3 lg:row-start-1 lg:translate-y-[93px]',
  'lg:col-start-1 lg:row-start-2 lg:translate-y-[93px]',
  'lg:col-start-2 lg:row-start-3',
  'lg:col-start-3 lg:row-start-2 lg:translate-y-[93px]',
  'lg:col-start-1 lg:row-start-3 lg:translate-y-[93px]',
  'lg:col-start-2 lg:row-start-4',
  'lg:col-start-3 lg:row-start-3 lg:translate-y-[93px]',
];
// data-current: 모바일 캐러셀 가운데 카드 — 사진 액자가 데스크탑 호버 모양이 된다(story-card CardPhotoFrame)
function StoryCardSlot({
  index,
  current,
  children,
}: {
  index: number;
  current: boolean;
  children: ReactNode;
}) {
  return (
    <li
      data-current={current || undefined}
      className={cn(
        'group/slot shrink-0 snap-center',
        MASONRY_PLACEMENT[index],
      )}
    >
      <div className="ty-story-reveal">{children}</div>
    </li>
  );
}
